import { useEffect } from 'react';
import { useIsFetching } from '@tanstack/react-query';
import { useUiStore } from '../store/ui.js';
import { useToastStore } from '../store/toast.js';
import { useEntities, useEmployees } from '../api/hooks.js';

// Atterrissage du changement de société.
//
// EntitySelect ouvre le changement (beginEntitySwitch) ; ce hook, monté
// dans AppShell, le referme quand les requêtes React Query déclenchées
// par la nouvelle clé sont retombées — avec un plancher pour que l'œil
// lise le changement même quand tout est en cache, et un plafond pour
// qu'une requête lente ne fige jamais l'écran.

export const SWITCH_MIN_MS = 350;
export const SWITCH_MAX_MS = 4000;
export const SWITCH_TOAST_MS = 2500;

export const ALL_ENTITIES_LABEL = 'Toutes les sociétés';

// enabled = false pour le manager recruteur : pas de sélecteur de
// société, et /entities + /employees lui sont fermés.
export function useEntitySwitchLanding(enabled = true): void {
  const sw = useUiStore((s) => s.entitySwitch);
  const end = useUiStore((s) => s.endEntitySwitch);
  const fetching = useIsFetching();
  const { data: entities } = useEntities(enabled);
  const { data: employees } = useEmployees(enabled);

  useEffect(() => {
    if (!sw) return undefined;
    const elapsed = Date.now() - sw.since;
    const delay = fetching > 0
      ? Math.max(0, SWITCH_MAX_MS - elapsed)
      : Math.max(0, SWITCH_MIN_MS - elapsed);
    const t = window.setTimeout(() => {
      end();
      const ent = entities?.find((e) => e.id === sw.targetId);
      const name = sw.targetId === 'all' ? ALL_ENTITIES_LABEL : (ent?.name ?? sw.targetId);
      const n = employees
        ? (sw.targetId === 'all' ? employees.length : employees.filter((e) => e.entityId === sw.targetId).length)
        : null;
      useToastStore.getState().push({
        tone: 'info',
        message: n == null ? `Vue : ${name}` : `Vue : ${name} · ${n} employé${n > 1 ? 's' : ''}`,
        durationMs: SWITCH_TOAST_MS,
      });
    }, delay);
    return () => window.clearTimeout(t);
  }, [sw, fetching, end, entities, employees]);
}
